import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import apiClient from "api/axiosConfig";
import SuccessModal from "components/Common/SuccessModal";

const AddLanguageModal = ({ show, onClose, onSuccess, templateId }) => {
  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchLanguages = async () => {
      setLoadingLanguages(true);
      setError("");
      try {
        const response = await apiClient.get(
          `/templates/${templateId}/languages/untranslated`
        );
        const formattedLanguages = response.data.data.map((lang) => ({
          value: lang.code,
          label: lang.name,
        }));
        setLanguages(formattedLanguages);
      } catch (err) {
        console.error("Error fetching languages:", err);
        setError("Failed to load languages. Please try again.");
      } finally {
        setLoadingLanguages(false);
      }
    };

    if (show && templateId) {
      fetchLanguages();
      setSelectedLanguages([]);
    }
  }, [show, templateId]);

  const handleSave = async () => {
    if (selectedLanguages.length === 0) {
      setError("Please select at least one language.");
      return;
    }

    setSavingChanges(true);
    setError("");

    try {
      const languageCodes = selectedLanguages.map((lang) => lang.value);
      const languageString = languageCodes.join(',');

      const response = await apiClient.post(`/templates/${templateId}/add-translations`, {
        target_languages: languageString,
      });

      const message = response?.data?.message || "Language added successfully!";
      setSuccessMessage(message);
      setSavingChanges(false);
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Error adding languages:", err);
      setError("Failed to add languages. Please try again.");
      setSavingChanges(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    onClose();
    onSuccess();
  };

  if (!show) {
    return null;
  }

  if (showSuccessModal) {
    return (
      <SuccessModal
        show={showSuccessModal}
        onClose={handleCloseSuccessModal}
        message={successMessage}
      />
    );
  }

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Language</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              <div className="mb-3">
                <label htmlFor="languageSelect" className="form-label">
                  Select Language
                </label>
                <Select
                  id="languageSelect"
                  isMulti
                  options={languages}
                  value={selectedLanguages}
                  onChange={setSelectedLanguages}
                  isDisabled={loadingLanguages}
                  placeholder={
                    loadingLanguages
                      ? "Loading languages..."
                      : languages.length === 0
                      ? "No new languages available."
                      : "Choose..."
                  }
                  classNamePrefix="react-select"
                />
                {selectedLanguages.length === 0 && error && (
                  <div className="invalid-feedback d-block">{error}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={savingChanges || selectedLanguages.length === 0}
              >
                {savingChanges ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

AddLanguageModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  templateId: PropTypes.number.isRequired,
};

export default AddLanguageModal;
